import NoDataFound from "components/common/NoDataFound";
import {
  Calendar,
  ChevronRight,
  Clock4,
  DollarSign,
  MapPin,
  Search,
  User,
  X,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import RequestImg from "assets/images/temp/request.webp";
import WhatsappIcon from "assets/images/icons/whatsapp.svg";
import { Form, Formik } from "formik";
import CustomLabel from "components/common/CustomLabel";
import CustomField from "components/common/CustomField";
import { updateCompanyShare } from "components/common/Validation";
import Paginations from "components/common/Paginations";
import DateRangePicker from "components/common/DateRangePicker";
import { Dropdown } from "primereact/dropdown";
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllWorkshopRequest,
  updateWorkshopRequestShare,
  updateWorkshopRequestStatus,
  updateNewWorkshopRequestCount,
  getNewWorkshopRequestCount
} from "store/actions/workspaceSlice";
import { workshopRequestStatus } from "components/constants/StatusKeys";
import { formatDate, getFormatDates } from "components/common/Utils";
import { getCityList } from "store/actions/citySlice";
import Filters from "components/common/Filters";
import SingleSelect from "components/common/SingleSelect";
import { showAlert } from "store/reducers/alertReducer";
import Spinners from "components/common/Spinners";
import { getBranchListWithCity } from "store/actions/branchSlice";
import primaryRiyal from "assets/images/icons/primaryRiyal.svg";

const WorkshopRequests = () => {
  const { t } = useTranslation();
  document.title = t("Workshop Requests | Work Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { search } = useLocation();
  const pageNumber = Number(new URLSearchParams(search).get("page")) || 1;
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const {
    isLoading,
    loading,
    workshopRequests,
    perPage,
    totalCount,
    totalPages,
    currentPage,
  } = useSelector((state) => state.Workspace);

  // Filters

  const statusOptions =
    workshopRequestStatus?.map((item) => ({
      name: item.label,
      value: item.value,
    })) || [];

  const [selectedFilters, setSelectedFilters] = useState({
    requestStatus: -1,
    branches: [],
  });

  const filters = [
    {
      key: "requestStatus",
      placeholder: "Status",
      options: statusOptions,
      type: "singleSelect",
    },
    { key: "branches", placeholder: "Branch", type: "branchCityFilter" },
  ];

  const [show, setShow] = useState(false);
  const [modalType, setModalType] = useState(null);

  const [selectedRequest, setSelectedRequest] = useState();

  const [shareValues, setShareValues] = useState({
    trainerShare: null,
    companyShare: null,
    tax: null,
  });

  const [selectedStatus, setSelectedStatus] = useState(null);
  const [comments, setComments] = useState(null);
  const [commentsAr, setCommentsAr] = useState(null);

  useEffect(() => {
    getAllData();

    const fetchBranchesWithCity = () => {
      dispatch(getBranchListWithCity());
    };

    fetchBranchesWithCity();
  }, []);

  const getAllData = (page, action) => {
    setSelectedRequest({});
    const { actionType } = action || {}; // SEARCH, RESET
    const { requestStatus, branches } = selectedFilters;

    const isReset = actionType === "RESET";

    const payload = {
      perPage,
      requestId: isReset ? "" : requestId || "",
      pageNo: page || pageNumber,
      status: -1,
      requestStatus: isReset ? -1 : requestStatus,
      orderBy: -1,
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
      branches: isReset ? [] : branches,
      dateRange: isReset ? {} : getFormatDates(dates),
    };
    dispatch(getAllWorkshopRequest(payload));
    dispatch(updateNewWorkshopRequestCount());
    dispatch(getNewWorkshopRequestCount());
    // navigate(
    //   `/bookings/workshops/workshops-requests?page=${page || pageNumber}`
    // );
  };

  useEffect(() => {
    if (!loading && workshopRequests?.length > 0) {
      setSelectedRequest((prev) =>
        prev?._id !== workshopRequests[0]._id ? workshopRequests[0] : prev
      );
    }
  }, [workshopRequests, loading]);

  const handleClose = () => {
    setShow(false);
    setModalType(null);
    setSelectedStatus(null);
    setShareValues({
      trainerShare: null,
      companyShare: null,
      tax: null,
    });
  };
  const handleShow = (type) => {
    setShow(true);
    setModalType(type);
  };

  const handleShareSubmit = (values, { resetForm }) => {
    const { trainerShare, companyShare, tax } = values;

    const total = Number(trainerShare) + Number(companyShare) + Number(tax);

    if (total !== 100) {
      dispatch(
        showAlert({ text: "Total share must be exact 100%", type: "error" })
      );
      return;
    }

    const payload = {
      ...values,
      id: selectedRequest?._id,
    };

    dispatch(updateWorkshopRequestShare(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          handleClose();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleStatusChange = () => {
    const payload = {
      id: selectedRequest?._id,
      status: selectedStatus,
      comments: comments || " ",
      commentsAr: commentsAr || " ",
    };
    dispatch(updateWorkshopRequestStatus(payload))
      .then((action) => {
        const { statusCode, success, message, data } = action?.payload;
        if (statusCode === 200 && success) {
          setSelectedRequest(data);
          dispatch(showAlert({ text: message, type: "success" }));
          handleClose();
          setComments(null);
          setCommentsAr(null);
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const [keyword, setKeyword] = useState("");
  const [dates, setDates] = useState(null);

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedFilters({ requestStatus: -1, branches: [] });
    getAllData(pageNumber, { actionType: "RESET" });
  };

  const handleEditShareClick = () => {
    handleShow("EDIT_SHARE");
    const { workShopDetails } = selectedRequest || {};
    const { trainerShare, companyShare, tax } = workShopDetails || {};
    setShareValues({
      trainerShare,
      companyShare,
      tax,
    });
  };

  return (
    <React.Fragment>
      <div className="page-content common_page workshop_request_list common_details_page">
        <Container fluid>
          <Row>
            <div className="g_between">
              <h1 className="main_title">{t("Workshop Requests")}</h1>
              {selectedRequest?._id && (
                <div className="d-flex gap-3">
                  <button
                    onClick={handleEditShareClick}
                    className="main_btn_2 edit_request_btn"
                  >
                    {t("Edit Share")}
                  </button>
                  <button
                    onClick={() =>
                      navigate(
                        `/bookings/workshops/edit-workshop-request?id=${selectedRequest?._id}`
                      )
                    }
                    className="main_btn_2 edit_request_btn"
                    disabled={
                      selectedRequest?.requestStatus === 2 ||
                      selectedRequest?.requestStatus === 4
                    }
                  >
                    {t("Edit Request")}
                  </button>
                </div>
              )}
            </div>
          </Row>
          <div className="common_wrapper common_filter">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
            />
            {loading ? (
              <Spinners />
            ) : (
              <Row>
                <Col xxl={4} xl={5}>
                  <div className="req_list">
                    {workshopRequests?.length === 0 ? (
                      <NoDataFound />
                    ) : (
                      workshopRequests?.map((item) => (
                        <div
                          className={`inner_section c_p ${
                            item._id === selectedRequest?._id ? "active" : ""
                          } `}
                          key={item._id}
                          onClick={() => {
                            setSelectedRequest(item);
                          }}
                        >
                          <div className="">
                            <div className="g_between">
                              <h2 className="fs_14">{item?.name || "-"}</h2>
                              <p
                                className={`status_tag ${
                                  workshopRequestStatus.find(
                                    (status) =>
                                      status.value === item?.requestStatus
                                  )?.className || ""
                                }`}
                              >
                                {workshopRequestStatus.find(
                                  (status) =>
                                    status.value === item?.requestStatus
                                )?.label || ""}
                              </p>
                            </div>
                            <div className="g_between align-items-end mt-1">
                              <div>
                                <p className="fs_12 mt-1 text_dark_gray">
                                  {t("Workshops Request By")}{" "}
                                  {item?.createdBy?.name ? (
                                    <Link
                                      to={`/customers/customer-information?id=${item?.createdBy?._id}`}
                                      className="text_primary text-decoration-underline"
                                    >
                                      {/* {item?.requestedBy} */}
                                      {item?.createdBy?.name}
                                    </Link>
                                  ) : (
                                    "-"
                                  )}
                                </p>
                                <p className="fs_12 text_dark_gray">
                                  {formatDate(item?.createdAt)}
                                </p>
                              </div>
                              <ChevronRight className="text_gray" />
                            </div>
                          </div>
                        </div>
                      ))
                    )}

                    <div
                      className="g_between"
                      style={{ maxWidth: "100%", overflowX: "auto" }}
                    >
                      {!loading && (
                        <Paginations
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={(page) => {
                            getAllData(page);
                          }}
                          totalCount={totalCount}
                        />
                      )}
                    </div>
                  </div>
                </Col>
                <Col xxl={8} lg={7}>
                  {!loading &&
                  selectedRequest &&
                  Object?.keys(selectedRequest)?.length === 0 ? null : (
                    <div className="req_details ">
                      <div className="images_section">
                        <div className="d-flex gap-3">
                          <div className="left_section">
                            {selectedRequest?.imageUrls?.[0] && (
                              <img src={selectedRequest.imageUrls[0]} alt="" />
                            )}
                          </div>
                          <div className="right_section">
                            {selectedRequest?.imageUrls
                              ?.slice(1, 3) // Select the second and third images
                              .map((url, index) => (
                                <img
                                  key={index}
                                  src={url}
                                  alt={`Workspace Image ${index + 2}`}
                                  className=""
                                />
                              ))}
                          </div>
                        </div>
                      </div>
                      <div className="g_between mt-3">
                        <h2 className="fs_24 text_black ">
                          {selectedRequest?.name || "-"}
                        </h2>
                        <p
                          className={`status_tag ${
                            workshopRequestStatus.find(
                              (status) =>
                                status.value === selectedRequest?.requestStatus
                            )?.className || ""
                          }`}
                        >
                          {workshopRequestStatus.find(
                            (status) =>
                              status.value === selectedRequest?.requestStatus
                          )?.label || ""}
                        </p>
                      </div>
                      <h3 className="fs_14 text_dark_gray mt_13">
                        {t("Workshops Request By")}{" "}
                        {selectedRequest?.createdBy?.name ? (
                          <Link
                            to={`/customers/customer-information?id=${selectedRequest?.createdBy?._id}`}
                            className="text_primary text-decoration-underline"
                          >
                            {selectedRequest?.createdBy?.name || "-"}
                          </Link>
                        ) : (
                          "-"
                        )}
                        <br />
                        {formatDate(selectedRequest?.createdAt)}
                      </h3>
                      <div className="other_details">
                        <p className="text_black fs_14">
                          <MapPin
                            className="text_primary"
                            width={15}
                            height={15}
                          />{" "}
                          {selectedRequest?.cityId?.name},{" "}
                          {selectedRequest?.branchId?.name}
                        </p>
                        <p className="text_black fs_14">
                          <Clock4
                            className="text_primary"
                            width={15}
                            height={15}
                          />{" "}
                          {selectedRequest?.selectedDateAndTimes?.length >
                            0 && (
                            <>
                              {selectedRequest?.selectedDateAndTimes[0]
                                ?.startTime?.start || ""}{" "}
                              -{" "}
                              {selectedRequest?.selectedDateAndTimes[0]
                                ?.startTime?.end || ""}{" "}
                              (
                              {selectedRequest?.selectedDateAndTimes[0]
                                ?.durationInHour || ""}
                              h)
                            </>
                          )}
                        </p>
                        <p className="text_black fs_14">
                          <Calendar
                            className="text_primary"
                            width={15}
                            height={15}
                          />{" "}
                          {selectedRequest?.selectedDateAndTimes?.length > 0 &&
                            formatDate(
                              selectedRequest?.selectedDateAndTimes[0]
                                ?.startDate || ""
                            )}
                        </p>
                        <p className="text_black fs_14">
                          <User
                            className="text_primary"
                            width={15}
                            height={15}
                          />{" "}
                          {selectedRequest?.workShopDetails?.numberOfSeats}{" "}
                          {t("Seats")}
                        </p>
                        <p className="text_black fs_14">
                          <img
                            src={primaryRiyal}
                            width={15}
                            height={15}
                            alt="riyal"
                            className="me-1"
                          />
                          {selectedRequest?.workShopDetails?.price}{" "}
                        </p>
                        {selectedRequest?.createdBy?.name && (
                          <p className="text_black fs_14">
                            <User
                              className="text_primary"
                              width={15}
                              height={15}
                            />{" "}
                            {selectedRequest?.createdBy?.name || "-"}
                          </p>
                        )}
                      </div>
                      <h2 className="fs_16 mt_24">{t("Description")}</h2>
                      <p className="text_black fs_14 desc_text">
                        {selectedRequest?.description}
                      </p>
                      <h2 className="fs_16 mt_24">{t("Topics")}</h2>
                      <p className="text_black fs_14 desc_text">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: selectedRequest?.topics,
                          }}
                        />
                      </p>
                      <h2 className="fs_16 mt_24">{t("Bank Information")}</h2>
                      <div className="bank_info_inner">
                        <div>
                          <h2 className="fs_12 text_dark_gray">
                            {t("Account Holder")}
                          </h2>
                          <p className="fs_14 black_color">
                            {selectedRequest?.workShopDetails?.bankInfo
                              ?.accountHolder || "-"}
                          </p>
                        </div>
                        <div>
                          <h2 className="fs_12 text_dark_gray">
                            {t("Bank Name")}
                          </h2>
                          <p className="fs_14 black_color">
                            {selectedRequest?.workShopDetails?.bankInfo
                              ?.bankLabel || "-"}
                          </p>
                        </div>
                        <div>
                          <h2 className="fs_12 text_dark_gray">{t("IBAN")}</h2>
                          <p className="fs_14 black_color">
                            {selectedRequest?.workShopDetails?.bankInfo?.IBAN ||
                              "-"}
                          </p>
                        </div>
                      </div>
                      <div className="contact_details">
                        <div className="g_between flex-wrap">
                          <div className="d-flex gap-5">
                            <div>
                              <h2 className="text_dark_gray fs_12">
                                {t("Name")}
                              </h2>
                              <p className="fs_14 text_black">
                                {selectedRequest?.createdBy?.name || "-"}
                              </p>
                            </div>
                            <div>
                              <h2 className="text_dark_gray fs_12">
                                {t("Number")}
                              </h2>
                              <p className="fs_14 text_black">
                                {selectedRequest?.createdBy?.phone || "-"}
                              </p>
                            </div>
                          </div>
                          <button
                            className="main_btn contact_btn"
                            onClick={() => {
                              handleShow("UPDATE_STATUS");
                              setSelectedStatus(selectedRequest?.requestStatus);
                            }}
                            disabled={
                              selectedRequest?.requestStatus === 2 ||
                              selectedRequest?.requestStatus === 4
                            } // DISABLE BUTTON IF CURRENT STATUS IS PUBLISHED/COMPLETED
                          >
                            {/* <img
                              src={WhatsappIcon}
                              alt="icon"
                              width={15}
                              height={15}
                            />{" "} */}
                            {t("Update Status")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </Col>
              </Row>
            )}
          </div>
        </Container>
      </div>
      <Modal centered show={show} onHide={handleClose} className="common_modal">
        <div className="modal_header g_between">
          <h2 className="fs_17">
            {modalType === "EDIT_SHARE"
              ? t("Edit Shares")
              : modalType === "UPDATE_STATUS"
              ? t("Update Status")
              : ""}
          </h2>
          <X
            onClick={handleClose}
            className="text_black c_p"
            width={20}
            height={20}
          />
        </div>
        <div className="modal_body">
          {modalType === "EDIT_SHARE" && (
            <Formik
              initialValues={shareValues}
              validationSchema={updateCompanyShare}
              enableReinitialize={true}
              onSubmit={handleShareSubmit}
            >
              {({ values, setFieldValue, errors, touched }) => (
                <Form>
                  <Row>
                    <Col lg={12}>
                      <div className="form_field mt_13 mb-0">
                        <CustomLabel
                          htmlFor="trainerShare"
                          label={t("Trainer's Share")}
                        />
                        <CustomField
                          name="trainerShare"
                          type="number"
                          placeholder={"55%"}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="form_field mt_13 mb-0">
                        <CustomLabel
                          htmlFor="companyShare"
                          label={t("Company Share")}
                        />
                        <CustomField
                          name="companyShare"
                          type="number"
                          placeholder={"30%"}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="form_field mt_13 mb-0">
                        <CustomLabel htmlFor="tax" label={t("Tax")} />
                        <CustomField
                          name="tax"
                          type="number"
                          placeholder={"15%"}
                          touched={touched}
                          errors={errors}
                        />
                      </div>
                    </Col>
                    <Col lg={12}>
                      <div className="g_between mt-4 gap-3">
                        <button
                          type="button"
                          className="cancel_btn w-50"
                          onClick={handleClose}
                        >
                          {t("Cancel")}
                        </button>
                        <button
                          type="submit"
                          className="main_btn w-50"
                          disabled={isLoading}
                        >
                          {t("Save")}
                        </button>
                      </div>
                    </Col>
                  </Row>
                </Form>
              )}
            </Formik>
          )}
          {modalType === "UPDATE_STATUS" && (
            <Row>
              <Col lg={12}>
                <div className="form_field mt_13 mb-0">
                  <CustomLabel htmlFor="status" label={t("Status")} />
                  <SingleSelect
                    options={workshopRequestStatus
                      ?.filter((item) => item.value !== 4 && item.value !== 5) // Exclude "Completed"
                      ?.map((item) => ({
                        name: item?.label,
                        value: item?.value,
                        disabled:
                          item?.value === 0 || // always disable value 0 (Under Review)
                          (item?.value === 3 &&
                            (selectedRequest?.requestStatus === 2 ||
                              selectedRequest?.requestStatus === 4)), // conditionally disable Rejected
                      }))}
                    value={selectedStatus}
                    placeholder={t("Select Status")}
                    handleSingleSelectChange={(e) => {
                      const newStatus = e.value;
                      setSelectedStatus(newStatus);
                      const message = workshopRequestStatus?.find(
                        (item) => item?.value === newStatus
                      )?.message;
                      const messageAr = workshopRequestStatus?.find(
                        (item) => item?.value === newStatus
                      )?.messageAr;
                      setComments(message);
                      setCommentsAr(messageAr);
                    }}
                    className={`workshop_req_status_dropdown  ${
                      workshopRequestStatus?.find(
                        (option) => option.value === selectedStatus
                      )?.className || ""
                    }`}
                    panelClassName="workshop_status_options"
                  />
                </div>
              </Col>
              <Col lg={12}>
                <div className="form_field mt_13 mb-0">
                  <CustomLabel
                    htmlFor="comments"
                    label={t("English Message")}
                  />
                  <textarea
                    name="comments"
                    value={comments}
                    onChange={(e) => setComments(e.target.value)}
                    placeholder="Message"
                  ></textarea>
                </div>
              </Col>
              <Col lg={12}>
                <div className="form_field mt_13 mb-0">
                  <CustomLabel htmlFor="comments" label={t("Arabic Message")} />
                  <textarea
                    name="commentsAr"
                    value={commentsAr}
                    onChange={(e) => setCommentsAr(e.target.value)}
                    placeholder="Message"
                    className="rtl"
                  ></textarea>
                </div>
              </Col>
              <Col lg={12}>
                <div className="g_between mt-4 gap-3">
                  <button
                    type="button"
                    className="cancel_btn w-50"
                    onClick={handleClose}
                  >
                    {t("Cancel")}
                  </button>
                  <button
                    type="submit"
                    className="main_btn g_center gap-1 w-50"
                    onClick={handleStatusChange}
                    disabled={
                      isLoading ||
                      selectedStatus === null ||
                      selectedStatus === selectedRequest.requestStatus
                    }
                  >
                    {/* <img src={WhatsappIcon} alt="icon" width={18} height={18} />{" "} */}
                    {t("Update")}
                  </button>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </Modal>
    </React.Fragment>
  );
};

export default WorkshopRequests;
